/**
 * 与 Prisma schema 中的枚举保持一一对应。
 *
 * 这里重新声明一份而不是从 @prisma/client 导入，是因为前端不应该依赖后端的
 * Prisma 运行时。改动枚举时两边都要改，apps/api/src/common/enum-parity.test.ts
 * 会做一致性校验。
 */
/*
 * 角色不再是枚举（CR-038）。它是 `Role` 表里的一行 —— 名字 + 勾上的权限 ——
 * 由超管在角色页配置。权限清单和相关判断在 ./permissions.ts。
 *
 * 这里曾经是 `UserRole` / `ROLE_RANK` / `hasAtLeastRole` / `canSeeAllSites` /
 * `canManageUser` / `assignableRoles`。四种角色写死在枚举里，客户说「写死不是那么方便」。
 */
export const UserStatus = {
    ACTIVE: 'ACTIVE',
    SUSPENDED: 'SUSPENDED',
};
export const SiteStatus = {
    ACTIVE: 'ACTIVE',
    ARCHIVED: 'ARCHIVED',
};
/** 单位（公司 / 分公司，CR-073）的状态。归档 = 不再往里建人、建工地，已有的数据照常能看 */
export const OrgStatus = {
    ACTIVE: 'ACTIVE',
    ARCHIVED: 'ARCHIVED',
};
export const AttachKind = {
    PHOTO: 'PHOTO',
    RECEIPT: 'RECEIPT',
    PDF: 'PDF',
    /** 日报和隐蔽工程的现场视频（CR-023）。不生成缩略图，也不烧水印 */
    VIDEO: 'VIDEO',
    OTHER: 'OTHER',
};
/**
 * 图纸的种类（CR-023）。
 *
 * schedule 复用图纸这张表，而不是新开一张 —— 它要的东西图纸全有：
 * 上传、版本链（传新的用 `supersedesId` 顶掉旧的）、签名下载、查看器。
 * 唯一的差别写在校验里：**schedule 上不能加图钉**，而且每个工地
 * 只认当前那一张（`list()` 本来就会滤掉被替代的旧版）。
 */
export const DrawingKind = {
    DRAWING: 'DRAWING',
    SCHEDULE: 'SCHEDULE',
};
/**
 * 进度表解析出来的那份数据发没发布（CR-080）。
 *
 * 草稿只有能换进度表的人看得到；发布之后工地上的人才看到甘特图。
 * 发布了就不再退回草稿 —— 改了直接生效，谁改的记审计。
 */
export const ScheduleStatus = {
    DRAFT: 'DRAFT',
    PUBLISHED: 'PUBLISHED',
};
/**
 * 进度表里一项是什么（CR-080）。原表靠颜色区分，颜色的意思每家公司不一样，
 * 所以落成数据时写明：施工 / 检查（停止点，盖住之前要过）/ 停工（节假日，不算工期）。
 */
export const ScheduleTaskType = {
    WORK: 'WORK',
    INSPECTION: 'INSPECTION',
    NONWORK: 'NONWORK',
};
/**
 * 附件挂在谁身上（`Attachment.ownerType`）。
 *
 * **这一列在数据库里是裸 String，不是 Prisma 枚举** —— 因为它是多态的，
 * 指向的几张表主键类型都不一样（账号和工地是整数，其余是 cuid）。
 * 所以它进不了 `enum-parity.test.ts` 的对照表，那份测试比的是真枚举。
 *
 * 收在这里的理由：改之前五个 service 各自手打字符串字面量，
 * 打错一个字母的后果是「附件存进去了，但按 ownerType 查永远查不出来」——
 * 不报错、不留痕，只是那条记录的照片凭空消失。
 */
export const AttachOwnerType = {
    DAILY_LOG: 'DAILY_LOG',
    HIDDEN_WORK: 'HIDDEN_WORK',
    MATERIAL: 'MATERIAL',
    DRAWING: 'DRAWING',
};
export const PunchType = {
    IN: 'IN',
    OUT: 'OUT',
};
export const PunchSource = {
    ONLINE: 'ONLINE',
    OFFLINE_SYNC: 'OFFLINE_SYNC',
    MANUAL_ADMIN: 'MANUAL_ADMIN',
};
/**
 * 「继续打卡」的理由（CR-015）。
 *
 * 定位拿不到、或者人在围栏外时，工人选一条理由就能继续打卡 ——
 * 打卡会进待复核，由管理员判断，而不是让他今天干脆打不上卡。
 *
 * `SITE_LOCATION_WRONG` 单独列出来很值钱：它把「工地坐标或半径配错了」
 * 这个系统性问题从一堆个案里暴露出来 —— 同一天三个人都选它，
 * 管理员就该去改工地设置，而不是逐条复核。
 */
export const PunchOverrideReason = {
    /** 手机搜不到定位信号 */
    GPS_UNAVAILABLE: 'GPS_UNAVAILABLE',
    /** 在室内 / 地下室 / 车库作业 */
    INDOOR_OR_UNDERGROUND: 'INDOOR_OR_UNDERGROUND',
    /** 在工地外围作业（围挡外、材料堆场） */
    WORKING_OUTSIDE_FENCE: 'WORKING_OUTSIDE_FENCE',
    /** 工地定位点或范围设置不准 */
    SITE_LOCATION_WRONG: 'SITE_LOCATION_WRONG',
    /** 手机故障 / 定位权限被关 */
    PHONE_ISSUE: 'PHONE_ISSUE',
    /** 受指派在别处作业（送料、取货、其他工点） */
    OFFSITE_TASK: 'OFFSITE_TASK',
    /** 其他，必须自己写清楚 */
    OTHER: 'OTHER',
};
export const PunchStatus = {
    APPROVED: 'APPROVED',
    PENDING_REVIEW: 'PENDING_REVIEW',
    REJECTED: 'REJECTED',
};
// RoundingMode / RoundScope / OvertimeBasis / PeriodType / PeriodStatus
// 这五个计薪枚举随工时结算一起下线（CR-013）。数据库里还留着，
// 但应用层不再引用，所以不在这里重复定义 —— 将来接回工资时再加回来。
export const PurchaseStatus = {
    PENDING: 'PENDING',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
    REIMBURSED: 'REIMBURSED',
};
/**
 * 「还没处理完」的采购状态：待批准、待报销（CR-049）。
 *
 * 这两种**不按日期筛，全部列出，最早的在前**。客户原话「应该全盘扫，这样才不会遗漏未处理的」——
 * 改之前采购页默认「本月」，上个月没批的、批了没付钱的，不改区间就看不见。
 * 成熟的报销系统（Zoho Expense、Expensify、SAP Concur、Ramp）都把「等人处理的」做成不限日期的队列，
 * 按日期筛只用于历史和统计。
 *
 * **规则在服务端**：采购列表、「我的」、下载都认这一份；前端只拿它决定显示（不传区间、换提示）。
 */
export const OPEN_PURCHASE_STATUSES = [PurchaseStatus.PENDING, PurchaseStatus.APPROVED];
export function isOpenPurchaseStatus(status) {
    return status != null && OPEN_PURCHASE_STATUSES.includes(status);
}
export const Locale = {
    ZH_CN: 'zh-CN',
    EN: 'en',
    // CR-055
    ES: 'es',
};
//# sourceMappingURL=enums.js.map